import React, { useState, useEffect } from 'react';
import { attendanceAPI, usersAPI, processingAPI } from '../../services/api';
import { format } from 'date-fns';
import { Edit2, ShieldCheck, RefreshCw, X, Search, AlertCircle, History, Download } from 'lucide-react';
import { getStatusBadge, getRowHighlightClass, DateColumnDisplay, formatMinutes } from '../../utils/attendanceUtils';
import { RawTransactionsPanel, AuditTrailPanel } from './AttendanceEditPanels';

const PAGE_SIZE = 50;

const AttendanceDashboard = () => {
  const [startDate, setStartDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [endDate, setEndDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [selectedUserIdFilter, setSelectedUserIdFilter] = useState('');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  const [allUsers, setAllUsers] = useState([]);
  const [records, setRecords] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncLog, setSyncLog] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);

  // Storing composite keys "userId_workday" since records span dates
  const [selectedRecords, setSelectedRecords] = useState(new Set());

  // Modals
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  const [bulkShiftModalOpen, setBulkShiftModalOpen] = useState(false);
  const [selectedBulkShift, setSelectedBulkShift] = useState('');

  const [historyModal, setHistoryModal] = useState(null); // { record, entries }
  const [editTxLoading, setEditTxLoading] = useState(false);
  const [editTxData, setEditTxData] = useState([]);
  const [editHistoryLoading, setEditHistoryLoading] = useState(false);
  const [editHistoryData, setEditHistoryData] = useState([]);

  // Fetch initial users list for filter dropdown
  useEffect(() => {
    const fetchUsersForDropdown = async () => {
      try {
        const response = await usersAPI.getUsers({ per_page: 1000, show_inactive: false });
        // The backend GET /users usually returns users inside response.data.users
        if (response.data && response.data.users) {
          setAllUsers(response.data.users);
        }
      } catch (err) {
        console.error("Failed to load users for filter dropdown", err);
      }
    };
    fetchUsersForDropdown();
  }, []);

  useEffect(() => {
    fetchRecords();
  }, [startDate, endDate, selectedUserIdFilter]);

  const fetchRecords = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const params = { start_date: startDate, end_date: endDate };
      if (selectedUserIdFilter) {
        params.user_id = selectedUserIdFilter;
      }

      const response = await attendanceAPI.getHrDailyRecords(params);

      // Sort records descending by date (latest first), and by user staff_no (ascending)
      const sortedRecords = response.data.records.sort((a, b) => {
        const dateDiff = new Date(b.workday) - new Date(a.workday);
        if (dateDiff !== 0) return dateDiff;
        return a.staff_no.localeCompare(b.staff_no);
      });

      setRecords(sortedRecords);
      setShifts(response.data.shifts);
      setSelectedRecords(new Set());
      setCurrentPage(1);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch attendance records');
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(records.length / PAGE_SIZE);
  const paginatedRecords = records.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const getRecordKey = (userId, workday) => `${userId}_${workday}`;

  const toggleRecordSelection = (userId, workday) => {
    const key = getRecordKey(userId, workday);
    const newSelection = new Set(selectedRecords);
    if (newSelection.has(key)) {
      newSelection.delete(key);
    } else {
      newSelection.add(key);
    }
    setSelectedRecords(newSelection);
  };

  // Selects / deselects the current page only
  const toggleAllSelection = () => {
    const pageKeys = paginatedRecords.map(r => getRecordKey(r.user_id, r.workday));
    const allPageSelected = pageKeys.every(k => selectedRecords.has(k));
    const newSelection = new Set(selectedRecords);
    if (allPageSelected) {
      pageKeys.forEach(k => newSelection.delete(k));
    } else {
      pageKeys.forEach(k => newSelection.add(k));
    }
    setSelectedRecords(newSelection);
  };

  const handleBulkFinalize = async () => {
    if (selectedRecords.size === 0) return;
    if (!window.confirm(`Are you sure you want to finalize ${selectedRecords.size} records?`)) return;

    setLoading(true);
    setError('');

    try {
      const updates = Array.from(selectedRecords).map(key => {
        const [userId, workday] = key.split('_');
        return {
          user_id: parseInt(userId),
          workday: workday,
          is_final: true,
          reason: 'HR Bulk Finalize'
        };
      });

      await attendanceAPI.hrBatchUpdateRecords({ updates });
      setSuccess(`Successfully finalized ${selectedRecords.size} records.`);
      fetchRecords();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to finalize records.');
      setLoading(false);
    }
  };

  const handleBulkShiftUpdate = async () => {
    if (!selectedBulkShift) return;

    setLoading(true);
    setError('');

    try {
      const updates = Array.from(selectedRecords).map(key => {
        const [userId, workday] = key.split('_');
        return {
          user_id: parseInt(userId),
          workday: workday,
          shift_id: parseInt(selectedBulkShift),
          reason: 'HR Bulk Shift Assignment'
        };
      });

      await attendanceAPI.hrBatchUpdateRecords({ updates });
      setSuccess(`Successfully updated shift for ${selectedRecords.size} records.`);
      setBulkShiftModalOpen(false);
      fetchRecords();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update shifts.');
      setLoading(false);
    }
  };

  const handleSingleRecordUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let clock_in = null;
      if (editingRecord.clock_in_time) {
        if (editingRecord.clock_in_time.includes('T')) {
          clock_in = editingRecord.clock_in_time;
        } else {
          clock_in = `${editingRecord.workday}T${editingRecord.clock_in_time}:00`;
        }
      }

      let clock_out = null;
      if (editingRecord.clock_out_time) {
        if (editingRecord.clock_out_time.includes('T')) {
          clock_out = editingRecord.clock_out_time;
        } else {
          clock_out = `${editingRecord.workday}T${editingRecord.clock_out_time}:00`;
        }
      }

      const updates = [{
        user_id: editingRecord.user_id,
        workday: editingRecord.workday,
        clock_in: editingRecord.clock_in ? editingRecord.clock_in : null,
        clock_out: editingRecord.clock_out ? editingRecord.clock_out : null,
        shift_id: editingRecord.shift_id,
        is_final: editingRecord.is_final,
        is_overridden: editingRecord.is_overridden,
        override_remark: editingRecord.is_overridden ? (editingRecord.override_remark || '') : null,
        reason: editingRecord.reason || (editingRecord.is_overridden ? editingRecord.override_remark : 'HR Manual Single Edit')
      }];

      await attendanceAPI.hrBatchUpdateRecords({ updates });
      setSuccess('Successfully updated record.');
      setEditModalOpen(false);
      fetchRecords();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update record.');
      setLoading(false);
    }
  };

  const handleSyncLeaves = async () => {
    setSyncing(true);
    setSyncLog([]);
    setError('');
    setSuccess('');
    try {
      const res = await processingAPI.syncLeaves();
      const jobId = res.data.job_id;
      const poll = setInterval(async () => {
        try {
          const statusRes = await processingAPI.getJobStatus(jobId);
          const job = statusRes.data;
          if (job.status === 'done') {
            clearInterval(poll);
            setSyncing(false);
            const result = job.result || {};
            setSyncLog(result.log_entries || []);
            setSuccess(
              `Leaves sync complete — ${result.synced ?? 0} synced, ${result.skipped ?? 0} skipped, ${result.errors ?? 0} errors`
            );
            fetchRecords();
          } else if (job.status === 'error') {
            clearInterval(poll);
            setSyncing(false);
            setError('Sync failed: ' + (job.message || 'Unknown error'));
          }
        } catch {
          clearInterval(poll);
          setSyncing(false);
          setError('Failed to check sync status');
        }
      }, 3000);
    } catch (err) {
      setSyncing(false);
      setError(err.response?.data?.error || 'Failed to start leaves sync');
    }
  };

  const handleExportCsv = () => {
    if (!records.length) return;
    const fmtTime = (iso) => {
      if (!iso) return '';
      return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
    };
    const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const dayName = (iso) => DAYS[new Date(iso + 'T00:00:00').getDay()];
    const showEmployee = !selectedUserIdFilter;
    const headers = [
      'Date', 'Day',
      ...(showEmployee ? ['Name', 'Staff No'] : []),
      'Shift', 'Clock In', 'Clock Out', 'Hours',
      'Late', 'Early Out', 'Holiday', 'Leave', 'Finalized',
      'Remark', 'Override Remark',
    ];
    const rows = records.map(r => [
      r.workday,
      dayName(r.workday),
      ...(showEmployee ? [r.name, r.staff_no] : []),
      r.shift_code || '',
      fmtTime(r.clock_in),
      fmtTime(r.clock_out),
      r.total_work_minutes ? (r.total_work_minutes / 60).toFixed(2) : '0.00',
      r.is_late ? 'Yes' : 'No',
      r.is_early_out ? 'Yes' : 'No',
      r.is_holiday ? 'Yes' : 'No',
      r.leave_code || '',
      r.is_final ? 'Yes' : 'No',
      r.remark || '',
      r.override_remark || '',
    ]);
    const csv = [headers, ...rows]
      .map(row => row.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
      .join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `attendance_${startDate}_to_${endDate}${selectedUserIdFilter ? `_${userSearchTerm.split(' ')[0]}` : ''}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleViewHistory = async (record) => {
    try {
      const res = await attendanceAPI.getRecordHistory(record.id);
      setHistoryModal({ record, entries: res.data.history });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load change history');
    }
  };

  const formatDisplayTime = (isoString) => {
    if (!isoString) return '-';
    return new Date(isoString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const toLocalISOForInput = (isoString) => {
    if (!isoString) return '';
    const d = new Date(isoString);
    const offset = d.getTimezoneOffset() * 60000;
    const localISOTime = (new Date(d - offset)).toISOString().slice(0, -1);
    return localISOTime.substring(0, 19); // preserve seconds to avoid spurious backend changes
  };

  const openEditModal = async (record) => {
    setEditingRecord({
      ...record,
      clock_in: toLocalISOForInput(record.clock_in),
      clock_out: toLocalISOForInput(record.clock_out),
      is_overridden: record.is_overridden || false,
      override_remark: record.override_remark || '',
      reason: ''
    });
    setEditTxData([]);
    setEditTxLoading(true);
    setEditHistoryData([]);
    setEditHistoryLoading(true);
    setEditModalOpen(true);

    const [txRes, histRes] = await Promise.allSettled([
      attendanceAPI.getRawTransactions(record.user_id, record.workday),
      attendanceAPI.getRecordHistory(record.id),
    ]);
    setEditTxData(txRes.status === 'fulfilled' ? txRes.value.data.transactions : []);
    setEditTxLoading(false);
    setEditHistoryData(histRes.status === 'fulfilled' ? histRes.value.data.history : []);
    setEditHistoryLoading(false);
  };

  // Calculate Summary
  const calculateSummary = () => {
    if (!records.length) return null;
    let totalMins = 0;
    let lateCount = 0;
    let missedCount = 0;
    let earlyOutCount = 0;

    // missedCount counts records where both clock_in and clock_out are null and is_weekday and is not is_holiday
    records.forEach(r => {
      totalMins += r.total_work_minutes || 0;
      if (r.is_late) lateCount++;
      if (r.is_early_out) earlyOutCount++;
      if (!r.clock_in && !r.clock_out && r.is_weekday && !r.is_holiday) missedCount++;
    });

    return {
      totalMins,
      lateCount,
      missedCount,
      earlyOutCount
    };
  };

  const summary = selectedUserIdFilter ? calculateSummary() : null;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
        <h2 className="text-xl font-bold text-gray-900">Records</h2>

        <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-3 w-full md:w-auto">
          {/* User Search */}
          <div className="relative min-w-[220px]" onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setShowUserDropdown(false); }}>
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              value={userSearchTerm}
              onChange={(e) => { setUserSearchTerm(e.target.value); setShowUserDropdown(true); }}
              onFocus={() => setShowUserDropdown(true)}
              placeholder="Search employee..."
              className="pl-9 pr-8 py-2 block w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
            />
            {(userSearchTerm || selectedUserIdFilter) && (
              <button
                className="absolute inset-y-0 right-0 pr-2 flex items-center text-gray-400 hover:text-gray-600"
                onMouseDown={(e) => { e.preventDefault(); setUserSearchTerm(''); setSelectedUserIdFilter(''); setShowUserDropdown(false); }}
              >
                <X className="h-4 w-4" />
              </button>
            )}
            {showUserDropdown && (
              <ul className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto text-sm">
                <li
                  className="px-3 py-2 cursor-pointer hover:bg-gray-100 text-gray-500"
                  onMouseDown={() => { setSelectedUserIdFilter(''); setUserSearchTerm(''); setShowUserDropdown(false); }}
                >
                  All Employees
                </li>
                {allUsers
                  .filter(u => !userSearchTerm || u.name.toLowerCase().includes(userSearchTerm.toLowerCase()) || u.staff_no.toLowerCase().includes(userSearchTerm.toLowerCase()))
                  .map(u => (
                    <li
                      key={u.id}
                      className="px-3 py-2 cursor-pointer hover:bg-blue-50 hover:text-blue-700"
                      onMouseDown={() => { setSelectedUserIdFilter(String(u.id)); setUserSearchTerm(`${u.name} (${u.staff_no})`); setShowUserDropdown(false); }}
                    >
                      {u.name} <span className="text-gray-400">({u.staff_no})</span>
                    </li>
                  ))
                }
              </ul>
            )}
          </div>

          <div className="flex items-center space-x-2 bg-white rounded-md border border-gray-300 px-2 shadow-sm">
            <span className="text-sm text-gray-500 font-medium">From</span>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border-0 focus:ring-0 sm:text-sm py-2 px-1 text-gray-900"
            />
          </div>

          <div className="flex items-center space-x-2 bg-white rounded-md border border-gray-300 px-2 shadow-sm">
            <span className="text-sm text-gray-500 font-medium">To</span>
            <input
              type="date"
              value={endDate}
              min={startDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border-0 focus:ring-0 sm:text-sm py-2 px-1 text-gray-900"
            />
          </div>

          <button
            onClick={fetchRecords}
            className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <RefreshCw className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">Refresh</span>
          </button>

          <button
            onClick={handleExportCsv}
            disabled={!records.length}
            className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <Download className="h-4 w-4 md:mr-2" />
            <span className="hidden md:inline">Export</span>
          </button>

          <button
            onClick={handleSyncLeaves}
            disabled={syncing}
            className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 md:mr-2 ${syncing ? 'animate-spin' : ''}`} />
            <span className="hidden md:inline">{syncing ? 'Syncing...' : 'Sync Leaves'}</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border-l-4 border-green-400 p-4">
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      {/* Leaves sync log — only shown when there are discrepancies */}
      {syncLog.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-300 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-yellow-300 flex items-center justify-between">
            <div className="flex items-center">
              <AlertCircle className="h-5 w-5 text-yellow-600 mr-2" />
              <span className="text-sm font-semibold text-yellow-800">
                Sync discrepancies ({syncLog.length} {syncLog.length === 1 ? 'entry' : 'entries'})
              </span>
            </div>
            <button onClick={() => setSyncLog([])} className="text-yellow-500 hover:text-yellow-700">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="overflow-x-auto max-h-64 overflow-y-auto">
            <table className="min-w-full text-xs divide-y divide-yellow-200">
              <thead className="bg-yellow-100">
                <tr>
                  {['Level', 'Staff No', 'Date', 'Reason'].map(h => (
                    <th key={h} className="px-3 py-2 text-left font-medium text-yellow-800 uppercase tracking-wider whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-yellow-100">
                {syncLog.map((entry, i) => (
                  <tr key={i} className="hover:bg-yellow-100">
                    <td className="px-3 py-2 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${
                        entry.level === 'ERROR'   ? 'bg-red-100 text-red-700' :
                        entry.level === 'SKIPPED' ? 'bg-orange-100 text-orange-700' :
                                                    'bg-yellow-200 text-yellow-800'
                      }`}>{entry.level}</span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">{entry.staff_no}</td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-700">{entry.date}</td>
                    <td className="px-3 py-2 text-gray-600">{entry.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Dynamic Summary Widget for Single User */}
      {summary && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex gap-6">
          <div>
            <p className="text-sm text-gray-500 font-medium pb-1">Total Hours Worked</p>
            <p className="text-2xl font-bold text-gray-900">{formatMinutes(summary.totalMins)} <span className="text-base font-normal text-gray-500">hrs</span></p>
          </div>
          <div className="border-l border-gray-200 pl-6">
            <p className="text-sm text-gray-500 font-medium pb-1">Lates</p>
            <p className="text-2xl font-bold text-red-600">{summary.lateCount}</p>
          </div>
          <div className="border-l border-gray-200 pl-6">
            <p className="text-sm text-gray-500 font-medium pb-1">Early Out</p>
            <p className="text-2xl font-bold text-red-600">{summary.earlyOutCount}</p>
          </div>
          <div className="border-l border-gray-200 pl-6">
            <p className="text-sm text-gray-500 font-medium pb-1">Missed Days</p>
            <p className="text-2xl font-bold text-gray-600">{summary.missedCount}</p>
          </div>
        </div>
      )}

      {/* Bulk Actions */}
      <div className="bg-white p-4 shadow rounded-lg flex justify-between items-center">
        <div className="text-sm text-gray-700">
          <span className="font-semibold">{selectedRecords.size}</span> records selected
        </div>
        <div className="space-x-3">
          <button
            onClick={() => setBulkShiftModalOpen(true)}
            disabled={selectedRecords.size === 0}
            className={`inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${selectedRecords.size === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            Change Shift
          </button>
          <button
            onClick={handleBulkFinalize}
            disabled={selectedRecords.size === 0}
            className={`inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 ${selectedRecords.size === 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <ShieldCheck className="h-4 w-4 mr-2" />
            Finalize
          </button>
        </div>
      </div>

      {/* Attendance Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="relative overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading records...</div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <input
                      type="checkbox"
                      checked={paginatedRecords.length > 0 && paginatedRecords.every(r => selectedRecords.has(getRecordKey(r.user_id, r.workday)))}
                      onChange={toggleAllSelection}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  {!selectedUserIdFilter && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Employee</th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Shift</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clock In</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Clock Out</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Hrs</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedRecords.map((record) => {
                  const recordKey = getRecordKey(record.user_id, record.workday);
                  return (
                    <tr key={recordKey} className={getRowHighlightClass(record.workday, record.is_final)}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={selectedRecords.has(recordKey)}
                          onChange={() => toggleRecordSelection(record.user_id, record.workday)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <DateColumnDisplay workday={record.workday} />
                      </td>
                      {!selectedUserIdFilter && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{record.name}</div>
                          <div className="text-sm text-gray-500">{record.staff_no}</div>
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {record.shift_name ? `${record.shift_code}` : 'Unassigned'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatDisplayTime(record.clock_in)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatDisplayTime(record.clock_out)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatMinutes(record.total_work_minutes)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(record)}
                      </td>
                      <td className="px-6 py-4 text-sm max-w-xs">
                        {record.latest_reason && <div className="text-gray-700 truncate" title={record.latest_reason}>{record.latest_reason}</div>}
                        {record.override_remark && <div className="text-teal-600 truncate" title={record.override_remark}><span className="font-medium">Excused:</span> {record.override_remark}</div>}
                        {!record.latest_reason && !record.override_remark && <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="inline-flex items-center gap-3">
                          <button
                            onClick={() => openEditModal(record)}
                            className="text-blue-600 hover:text-blue-900"
                            title="Edit record"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          {record.id && (
                            <button
                              onClick={() => handleViewHistory(record)}
                              className="text-gray-400 hover:text-gray-700"
                              title="View change history"
                            >
                              <History className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border border-gray-200 rounded-lg shadow-sm">
          <p className="text-sm text-gray-700">
            Showing <span className="font-medium">{(currentPage - 1) * PAGE_SIZE + 1}</span>–
            <span className="font-medium">{Math.min(currentPage * PAGE_SIZE, records.length)}</span> of{' '}
            <span className="font-medium">{records.length}</span> records
            {selectedRecords.size > 0 && (
              <span className="ml-2 text-blue-600">({selectedRecords.size} selected across all pages)</span>
            )}
          </p>
          <div className="flex items-center space-x-1">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 rounded border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
              .reduce((acc, p, idx, arr) => {
                if (idx > 0 && p - arr[idx - 1] > 1) acc.push('...');
                acc.push(p);
                return acc;
              }, [])
              .map((item, idx) =>
                item === '...' ? (
                  <span key={`ellipsis-${idx}`} className="px-2 py-1 text-sm text-gray-400">…</span>
                ) : (
                  <button
                    key={item}
                    onClick={() => setCurrentPage(item)}
                    className={`px-3 py-1 rounded border text-sm ${
                      currentPage === item
                        ? 'bg-blue-600 border-blue-600 text-white font-medium'
                        : 'border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {item}
                  </button>
                )
              )}
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 rounded border border-gray-300 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Edit + Raw Transactions Modal (side-by-side) */}
      {editModalOpen && editingRecord && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl flex flex-col max-h-[90vh]">

            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-medium text-gray-900">
                  {editingRecord.name} — {editingRecord.workday}
                </h3>
                {editingRecord.is_final && (
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                    Finalised
                  </span>
                )}
              </div>
              <button onClick={() => setEditModalOpen(false)} className="text-gray-400 hover:text-gray-500">
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Body */}
            <div className="flex flex-1 min-h-0">

              {/* Left: Raw Transactions */}
              <div className="w-5/12 border-r border-gray-200 flex flex-col min-h-0">
                <RawTransactionsPanel loading={editTxLoading} data={editTxData} workday={editingRecord.workday} />
              </div>

              {/* Right: Edit Form + Audit Trail */}
              <div className="w-7/12 flex flex-col min-h-0">
                <div className="overflow-y-auto flex-1 min-h-0">
                {editingRecord.is_final && (
                  <div className="mx-4 mt-4 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 flex-shrink-0">
                    <p className="text-xs text-amber-700 font-medium">Finalised — fields are read-only. Uncheck "Mark as Final" below to enable editing.</p>
                  </div>
                )}
                <form onSubmit={handleSingleRecordUpdate} className="p-4 space-y-4">

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Clock In</label>
                    <input
                      type="datetime-local"
                      step="1"
                      disabled={editingRecord.is_final}
                      value={editingRecord.clock_in || ''}
                      onChange={(e) => setEditingRecord({ ...editingRecord, clock_in: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:bg-gray-50 disabled:text-gray-400"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Clock Out</label>
                    <input
                      type="datetime-local"
                      step="1"
                      disabled={editingRecord.is_final}
                      value={editingRecord.clock_out || ''}
                      onChange={(e) => setEditingRecord({ ...editingRecord, clock_out: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:bg-gray-50 disabled:text-gray-400"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">Shift</label>
                    <select
                      disabled={editingRecord.is_final}
                      value={editingRecord.shift_id || ''}
                      onChange={(e) => setEditingRecord({ ...editingRecord, shift_id: parseInt(e.target.value) })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:bg-gray-50 disabled:text-gray-400"
                    >
                      <option value="">Select Shift</option>
                      {shifts.map(s => (
                        <option key={s.id} value={s.id}>{s.name} ({s.code})</option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={editingRecord.is_final}
                      onChange={(e) => setEditingRecord({ ...editingRecord, is_final: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">Mark as Final</label>
                  </div>

                  <div className="border-t border-gray-100 pt-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="is_overridden"
                        disabled={editingRecord.is_final}
                        checked={editingRecord.is_overridden}
                        onChange={(e) => setEditingRecord({
                          ...editingRecord,
                          is_overridden: e.target.checked,
                          override_remark: e.target.checked ? editingRecord.override_remark : ''
                        })}
                        className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded disabled:opacity-50"
                      />
                      <label htmlFor="is_overridden" className="ml-2 block text-sm text-gray-900">
                        Excuse late / early out
                      </label>
                    </div>
                    {editingRecord.is_overridden && (
                      <div className="mt-2">
                        <input
                          type="text"
                          required
                          disabled={editingRecord.is_final}
                          placeholder="Required — reason for excusing (e.g. Medical certificate)"
                          value={editingRecord.override_remark}
                          onChange={(e) => setEditingRecord({ ...editingRecord, override_remark: e.target.value })}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-teal-500 focus:ring-teal-500 sm:text-sm disabled:bg-gray-50 disabled:text-gray-400"
                        />
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Reason (Audit Trail)
                      {editingRecord.is_overridden && (
                        <span className="ml-1 text-xs text-gray-400 font-normal">— defaults to excuse remark if left blank</span>
                      )}
                    </label>
                    <input
                      type="text"
                      required={!editingRecord.is_overridden && !editingRecord.is_final}
                      disabled={editingRecord.is_final}
                      placeholder={editingRecord.is_overridden ? 'Optional — leave blank to use excuse remark' : 'Required rationale for edit'}
                      value={editingRecord.reason}
                      onChange={(e) => setEditingRecord({ ...editingRecord, reason: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm disabled:bg-gray-50 disabled:text-gray-400"
                    />
                  </div>

                  <div className="pt-4 flex justify-between items-center">
                    <button
                      type="button"
                      disabled={editingRecord.is_final}
                      onClick={() => setEditingRecord({ ...editingRecord, clock_in: '', clock_out: '' })}
                      className="px-4 py-2 border border-red-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
                    >
                      Purge
                    </button>
                    <div className="flex space-x-3">
                      <button
                        type="button"
                        onClick={() => setEditModalOpen(false)}
                        className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={loading}
                        className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                      >
                        Save Changes
                      </button>
                    </div>
                  </div>

                </form>
                </div>

                {/* Audit Trail */}
                <div className="border-t border-gray-200 h-48 flex-shrink-0 flex flex-col min-h-0">
                  <AuditTrailPanel loading={editHistoryLoading} data={editHistoryData} />
                </div>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* Change History Modal */}
      {historyModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Change History</h3>
                <p className="text-sm text-gray-500 mt-0.5">
                  {historyModal.record.name} &middot; {historyModal.record.workday}
                </p>
              </div>
              <button onClick={() => setHistoryModal(null)} className="text-gray-400 hover:text-gray-500">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1">
              {historyModal.entries.length === 0 ? (
                <div className="p-8 text-center text-gray-400 text-sm">No changes recorded yet.</div>
              ) : (
                <table className="min-w-full text-sm divide-y divide-gray-200">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      {['Date / Time', 'Changed By', 'Fields Changed', 'Reason'].map(h => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {historyModal.entries.map(entry => {
                      const LABELS = {
                        clock_in: 'Clock In', clock_out: 'Clock Out',
                        shift_id: 'Shift', is_final: 'Finalized',
                        is_overridden: 'Excused', override_remark: 'Excuse Remark',
                      };
                      const fmtVal = (key, val) => {
                        if (val === null || val === undefined) return '—';
                        if (key === 'clock_in' || key === 'clock_out') {
                          const d = new Date(val);
                          return isNaN(d) ? val : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                        }
                        if (typeof val === 'boolean') return val ? 'Yes' : 'No';
                        return String(val);
                      };
                      const diffs = Object.keys(entry.new_values || {}).filter(
                        k => JSON.stringify(entry.old_values?.[k]) !== JSON.stringify(entry.new_values?.[k])
                      );
                      return (
                        <tr key={entry.id} className="hover:bg-gray-50 align-top">
                          <td className="px-4 py-3 whitespace-nowrap text-gray-500">
                            {new Date(entry.updated_at).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-gray-700">{entry.updated_by}</td>
                          <td className="px-4 py-3 text-gray-700">
                            {diffs.length === 0 ? (
                              <span className="text-gray-400 italic">—</span>
                            ) : (
                              <ul className="space-y-0.5">
                                {diffs.map(k => (
                                  <li key={k}>
                                    <span className="font-medium">{LABELS[k] || k}:</span>{' '}
                                    <span className="text-red-500">{fmtVal(k, entry.old_values?.[k])}</span>
                                    {' → '}
                                    <span className="text-green-600">{fmtVal(k, entry.new_values?.[k])}</span>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-500">{entry.reason || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bulk Shift Modal */}
      {bulkShiftModalOpen && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">
                Bulk Change Shift
              </h3>
              <button onClick={() => setBulkShiftModalOpen(false)} className="text-gray-400 hover:text-gray-500">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Select new shift for {selectedRecords.size} records</label>
                <select
                  value={selectedBulkShift}
                  onChange={(e) => setSelectedBulkShift(e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                >
                  <option value="">Select Shift</option>
                  {shifts.map(s => (
                    <option key={s.id} value={s.id}>{s.name} ({s.code})</option>
                  ))}
                </select>
              </div>

              <div className="pt-4 flex justify-end space-x-3">
                <button
                  onClick={() => setBulkShiftModalOpen(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleBulkShiftUpdate}
                  disabled={loading || !selectedBulkShift}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceDashboard;
